import React from "react";

const sections = [
  {
    title: "Introduction",
    body: `This is the Privacy Policy of EBC Bridge Private Limited (“EBC”), which operates under the brand name “Solveesy”. The Policy applies to all Solveesy software applications, websites, products and services.

As a general rule, users of our software applications, websites, products and services are anonymous to us, and we have no feasible ways to identify you. However, certain categories of data may be collected while using our applications and services, and some of this information may be considered “personal data” by the law. This Privacy Policy governs your access of the Solveesy service, regardless of how you access it, and by using our services, you consent to the collection, transfer, processing, storage, disclosure and other uses described in this Privacy Policy. The exact categories of data we collect, why we collect it, and your opt-out choices depend on the application or service you use.

When we post changes to this Privacy Policy, we will include the date when the Policy was last updated. If we significantly change this Policy, we will notify you about the upcoming change via our website or using e-mail and/or in-app notifications. We encourage you to review this Privacy Policy periodically.`,
  },
  {
    title: "User-submitted data",
    body: `Certain pieces of information we will only process when you submit it to us.

For example, to create a Solveesy account, we may ask for a preferred username and an e-mail address (to authenticate the request and for password recovery). Alternatively, you can use your Google Mail (“Gmail”), Facebook or LinkedIn account to sign in to a Solveesy account and access our services with the same profile. In this case we may collect your name and email address from these social networks. You can delete or modify the submitted data using your profile page. We may retain your data for some time after you’ve discontinued your Solveesy account in case you change your mind.

When you use the service, we automatically record information from your device, its software, and your activity using the services. This may include the device’s Internet Protocol (“IP”) address, browser type, the web page visited before you came to our website, search terms you submit to our website, geographic location, identification numbers associated with your devices, date and time stamps associated with activity on the website, system configuration information, metadata concerning files you upload to Solveesy’s platform, and other interactions with the service.

Note also that Solveesy allows you to sync your browser data, such as data entries, between devices that have Solveesy applications and services installed on them. Profile sync relies on your Solveesy account and follows the same privacy approach.

We will use this data only to provide the services you have requested and will not retain your data any longer than necessary to fulfill that specific purpose.`,
  },
  {
    title: "Usage statistics",
    body: `This information helps us to improve our products and services. We have no practical way to use this information to identify you personally. We will not store this usage data any longer than necessary to fulfill the said purpose.

We use this information for certain legitimate business purposes, namely:
1. To understand better how people interact with our applications and services;
2. To enhance, modify, personalize, or otherwise improve applications and to provide relevant value-added services that will enable you to take informed decisions
3. To determine the effectiveness of promotional campaigns and advertising;
4. To detect, debug and fix crashes in our applications and services;
5. To prevent security breaches and abuse.`,
  },
  {
    title: "Disclosure of your personal information",
    body: `As a rule, we don’t share your personal information outside the company.

In this regard, you hereby provide consent to share your personal information with third parties in limited circumstances, including:
1. with your consent; or
2. to a vendor and/or partner who meets our data protection standards; or
3. when we have a good faith belief it is required by law, such as pursuant to a legal process.

EBC collects such information only insofar as is necessary to fulfil the purpose of the interaction with EBC. EBC does not disclose personal information other than as described above. You can always refuse to supply your personal information, with the caveat that it may prevent you from engaging in certain websites or mobile application related activities.`,
  },
  {
    title: "Cookies",
    body: `We also use cookies to collect information and improve our services. We may use persistent cookies to save your registration ID and log-in password for future logins to the service. We may use “session ID cookies” to enable certain features of the service, to better understand how you interact with the service and to monitor aggregate usage and web traffic routing on the service. You can instruct your browser, by changing its options, to stop accepting cookies or to prompt you before accepting a cookie from the websites you visit. If you would like to reject third-party cookies, please configure your browser’s settings accordingly. If you do not accept cookies, however, you may not be able to use all aspects of the Service.`,
  },
  {
    title: "Third parties",
    body: `EBC utilizes third-party vendors and hosting partners, such as Amazon Web Services (“AWS”) and Google Cloud Platform (“GCP”) for hardware, software, networking, storage, and/or ancillary technology required to run Solveesy.

The use of third-party vendors, hosting partners and/or platforms such as AWS and GCP, and the privacy practices of these platforms, are governed by separate terms, conditions and policies for which EBC is not responsible. You should review AWS’s and GCP’s terms, conditions and policies where you will find more details on how information about you is used on their platforms and how you can set your privacy preferences.`,
  },
  {
    title: "Business transfers",
    body: `If we merge with another corporation, or in the unlikely event go out of business or enter bankruptcy, such that your information will become subject to a different privacy policy, we will notify you before the transfer and/or bankruptcy. You can opt out of the new policy by deleting your account during the said notice period.`,
  },
  {
    title: "Your rights",
    body: `We will honor your rights under local applicable data protection/privacy law(s). In response to a request, we will ask you to verify your identity when necessary, and to provide information that helps us to understand your request better. If we do not comply with your request, whether in whole or in part, we will explain why.`,
  },
  {
    title: "Contact us",
    body: `We are very open about privacy and recognise your trust as a great value. If you have any questions about this policy or any privacy related issues in our applications and/or services, feel free to contact us at operations@solveesy.in`,
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-14">
        <header className="mb-10 border-b border-gray-200 pb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Privacy policy</h1>
          <p className="mt-2 text-sm text-gray-500">
            Solveesy is operated by EBC Bridge Private Limited
          </p>
        </header>

        {/* <nav className="mb-10">
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {sections.map((section) => (
              <li key={section.title}>
                <a
                  href={`#${slugify(section.title)}`}
                  className="text-gray-600 hover:text-gray-900 underline underline-offset-2"
                >
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav> */}

        <main className="space-y-10">
          {sections.map((section) => (
            <section key={section.title} id={slugify(section.title)}>
              <h2 className="text-lg font-medium text-gray-900 mb-3">
                {section.title}
              </h2>
              <div className="text-gray-700 leading-relaxed whitespace-pre-line text-sm">
                {section.body}
              </div>
            </section>
          ))}
        </main>

        <footer className="mt-14 pt-6 border-t border-gray-200 text-xs text-gray-400">
          Questions? Contact us at{" "}
          <a
            href="mailto:operations@solveesy.in"
            className="text-gray-600 underline underline-offset-2"
          >
            operations@solveesy.in
          </a>
        </footer>
      </div>
    </div>
  );
}

function slugify(text : any) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}